import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  usePagination,
  useExpanded
} from "react-table";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { Checkbox } from "../../../../components/common/Checkbox";
import { COLUMNS } from "./Columns";
import { Link, useNavigate } from "react-router-dom";
import { showToast, showAlert } from "../../../../utils/utils";
import TableActionBtnGroup from "../tableactionbtngroup/TableActionBtnGroup";
import DualPagination from "../../../common/DualPagination";
import "./usermanagementtable.scss";
import {
  INACTIVE,
  PAGE_INDEX,
  PAGE_SIZE,
  showToastType,
  TRUE
} from "../../../../utils/constants/appConstants";
import {
  userManage,
  alertActions,
  commonErrMsg,
  action,
  noDataPresent
} from "../../../../locales/en";
import {
  getSlectedDeletedURL,
  getUserManagementActivateURL,
  getUserManagementDeleteURL
} from "../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../utils/axiosPrivate";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import UserManagmentActionHeader from "./UserManagmentActionHeader";
import SortableHeaderCell from "../../../common/SortableHeaderCell";
import { useColumnSort } from "../../../../hooks/useColumnSort";
import { userManagementLoadUserDataThunk } from "../../../../redux/userManagementSlice";
import { useDispatch, useSelector } from "react-redux";
import { DEACTIVATE_TOOLTIP_CONFIG } from "../../../../utils/constants/tooltipsConfig";
import { DEACTIVATED_USER, EDIT_ROLE } from "../../../../route/paths";

const SelectionHeader = ({ getToggleAllRowsSelectedProps }) => (
  <Checkbox {...getToggleAllRowsSelectedProps()} className="headercheckBox" />
);
SelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func
};

const SelectionCellWrapper = ({ row }) => (
  <Checkbox {...row.getToggleRowSelectedProps()} className="rowcheckBox" />
);
SelectionCellWrapper.propTypes = {
  row: PropTypes.object.isRequired
};

const DeactivateUserTable = () => {
  const dispatch = useDispatch();
  const { userManagementLoading } = useSelector(
    (state) => state.userManagement
  );
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const baseColumns = useMemo(() => COLUMNS, []);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const history = useNavigate();

  const SelectionCell = useCallback(
    ({ row }) => <SelectionCellWrapper row={row} />,
    []
  );

  const initialState = {
    id: null,
    fullname: null,
    primaryEmail: null,
    createdAt: null,
    updatedAt: null
  };
  const { columnOrders, handleSort } = useColumnSort(initialState);
  const sortableColumns = Object.keys(initialState);

  const tableColumns = useMemo(() => {
    if (data && data.length > 0) {
      return [
        {
          id: "selection",
          Header: SelectionHeader,
          Cell: SelectionCell
        },
        ...baseColumns
      ];
    }
    return baseColumns;
  }, [data, baseColumns, SelectionCell]);
  SelectionCell.propTypes = {
    row: PropTypes.object.isRequired
  };

  const loadDeactivatedUsers = async (pageNumber, size) => {
    try {
      const params = {
        page: pageNumber,
        size,
        query: searchQuery,
        status: INACTIVE,
        id: columnOrders.id,
        name: columnOrders.fullname,
        email: columnOrders.primaryEmail,
        createdAt: columnOrders.createdAt,
        updatedAt: columnOrders.updatedAt
      };
      const response = await dispatch(
        userManagementLoadUserDataThunk({ params })
      ).unwrap();
      const apiData = response?.data?.data || [];
      setData(apiData);
      setTotalPages(response.data.pageInfo.totalPages);
    } catch {
      //handle error
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    selectedFlatRows
  } = useTable(
    {
      columns: tableColumns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE },
      autoResetSelectedRows: false,
      getRowId: (row) => row.id
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  useEffect(() => {
    loadDeactivatedUsers(pageIndex, pageSize);
  }, [pageIndex, pageSize, searchQuery, JSON.stringify(columnOrders)]);

  const toggleRow = (rowId) =>
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );

  const handleRowClick = (row) => {
    const rowId = row.id;
    const newSelectedRowIds = new Set(selectedRowIds);

    if (newSelectedRowIds.has(rowId)) {
      newSelectedRowIds.delete(rowId);
    } else {
      newSelectedRowIds.add(rowId);
    }
    setSelectedRowIds(newSelectedRowIds);
  };

  const handleDeleteClick = async (rowId) => {
    try {
      const result = await showAlert(
        userManage?.errMsg.item1,
        userManage?.errMsg.item2,
        alertActions.warning,
        alertActions.Confirm,
        alertActions.Cancel
      );

      if (result.isConfirmed) {
        await privateAxios.patch(getUserManagementDeleteURL(rowId));

        loadDeactivatedUsers();
        history(DEACTIVATED_USER);
        showToast(userManage.errMsg.item3, showToastType.success);
      }
    } catch {
      showToast(commonErrMsg.item1, showToastType.error);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      const confirmed = await showAlert(
        userManage.errMsg.item1,
        userManage.errMsg.item4,
        alertActions.warning,
        alertActions.Confirm,
        alertActions.Cancel
      );
      if (confirmed.isConfirmed) {
        const selectedIds = selectedFlatRows.map((row) => row.original.id);
        try {
          await privateAxios.post(getSlectedDeletedURL, selectedIds);
          showToast(userManage.errMsg.item3, showToastType.success);

          setSelectedRowIds(new Set());
          state.selectedRowIds = {};
          loadDeactivatedUsers(pageIndex, pageSize);
        } catch {
          showToast(commonErrMsg.item1, showToastType.error);
        }
      }
    } catch {
      showToast(commonErrMsg.item1, showToastType.error);
    }
  };

  const handleActiveUserClick = async (rowId) => {
    try {
      const response = await privateAxios.patch(
        getUserManagementActivateURL(rowId)
      );
      const { message } = response.data;
      loadDeactivatedUsers();
      showToast(message, showToastType.success);
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      gotoPage(0);
      setSearchQuery(trimmedQuery);
    }
  };

  return (
    <>
      <UserManagmentActionHeader
        handleDeleteSelected={handleDeleteSelected}
        handleKeyDown={handleKeyDown}
        handleSearchChange={handleSearchChange}
        disabled={userManagementLoading}
        selectedFlatRows={selectedFlatRows}
        tempSearchQuery={tempSearchQuery}
        allUsers={TRUE}
        btnText={userManage.batchDelete}
      />
      <table
        className="mt-2 mb-3 usermangement-table-wrap"
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                {headerGroup.headers.map((column) => {
                  const isSortable = sortableColumns.includes(column.id);
                  return (
                    <SortableHeaderCell
                      key={column.id}
                      column={column}
                      isSortable={isSortable}
                      columnOrders={columnOrders}
                      handleSort={handleSort}
                    />
                  );
                })}
                <th>{action}</th>
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {userManagementLoading && (
            <TableSkeleton rows={10} columns={visibleColumnCount} />
          )}
          {!userManagementLoading && page.length === 0 && (
            <tr>
              <td colSpan={baseColumns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!userManagementLoading &&
            page.length > 0 &&
            page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              const isSelected = row.isSelected;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`parentrow ${isSelected ? "selected" : ""}`}
                    onClick={() => handleRowClick(row)}
                    {...row.getRowProps()}
                  >
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={(e) => toggleRow(row.id, e)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    {row.cells.map((cell) => {
                      const cellValue = cell.value;
                      let cellContent = cellValue;
                      if (cell.column.id === "roles") {
                        if (cellValue && cellValue.length > 0) {
                          cellContent = (
                            <ul className="permissions_content list-inline mb-0">
                              {cellValue.map((role) => (
                                <li
                                  className="list-inline-item mb-1"
                                  key={role.id}
                                >
                                  <Link
                                    to={EDIT_ROLE(role.id)}
                                    title={userManage.goToRole}
                                    className={`badge border border-success text-success rounded-pill ${role.fullAccess ? "bg-success text-white" : ""}`}
                                  >
                                    {role.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          );
                        } else {
                          cellContent = (
                            <ul className="permissions_content list-inline mb-0">
                              <li className="list-inline-item">
                                <span className="badge border border-danger bg-danger rounded-pill">
                                  {userManage.unassigned}
                                </span>
                              </li>
                            </ul>
                          );
                        }
                      }
                      return (
                        <td
                          key={cell.getCellProps().key}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell", { value: cellContent })}
                        </td>
                      );
                    })}
                    <td>
                      <TableActionBtnGroup
                        activateUser={() =>
                          handleActiveUserClick(row.original.id)
                        }
                        onDelete={() => handleDeleteClick(row.original.id)}
                        tooltips={DEACTIVATE_TOOLTIP_CONFIG}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={baseColumns.length + 2}>
                        <div className="tdbody">
                          {baseColumns.map((column) => (
                            <span
                              key={column.accessor}
                              data-label={column.Header}
                            >
                              {String(row.original[column.accessor])}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
        />
      )}
    </>
  );
};

export default DeactivateUserTable;
